/**
 * HTTP layer: one function, `api()`, that calls a named endpoint.
 *
 * - JSON in, JSON out, `{ data }` envelopes unwrapped by the caller.
 * - Same-origin cookies; the CSRF token from `/api/me` is sent on writes.
 * - Errors become ApiError(status, code, message) with the server's message.
 * - `lossless: true` parses and serialises with lossless-json so exact decimal
 *   strings (deal-model money) survive; `idempotencyKey` is sent as a header;
 *   `form` sends multipart (uploads) instead of JSON.
 */
import { parse as parseLossless, stringify as stringifyLossless } from 'lossless-json'
import { endpoints, methodOf, resolve, type EndpointName, type PathParams, type QueryParams } from './endpoints'

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

let csrfToken: string | null = null
export function setCsrfToken(token: string | null): void { csrfToken = token }
export function getCsrfToken(): string | null { return csrfToken }

export interface ApiCall {
  params?: PathParams
  query?: QueryParams
  body?: unknown
  form?: FormData
  signal?: AbortSignal
  lossless?: boolean
  idempotencyKey?: string
  /** Override the registry method (e.g. DELETE with a body on a PUT route). */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
}

export async function api<T = unknown>(name: EndpointName, call: ApiCall = {}): Promise<T> {
  const method = call.method ?? methodOf(name)
  const url = resolve(name, call.params, call.query)
  const unsafe = !['GET', 'HEAD', 'OPTIONS'].includes(method)
  if (unsafe && !csrfToken) throw new ApiError(401, 'authentication_required', 'Sign in required before making changes')
  const jsonBody = call.body !== undefined ? (call.lossless ? stringifyLossless(call.body) : JSON.stringify(call.body)) : undefined
  const response = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(jsonBody !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(unsafe && csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      ...(call.idempotencyKey ? { 'Idempotency-Key': call.idempotencyKey } : {}),
    },
    body: call.form ?? jsonBody,
    signal: call.signal,
  })
  const text = await response.text().catch(() => '')
  let body: unknown = null
  try { body = text ? (call.lossless ? parseLossless(text) : JSON.parse(text)) : null } catch { body = null }
  if (!response.ok) {
    const envelope = body as { error?: { code?: string; message?: string } } | null
    const message = envelope?.error?.message ?? (text.trim() ? text.trim().slice(0, 300) : `Request failed with status ${response.status}`)
    throw new ApiError(response.status, envelope?.error?.code ?? 'http_error', message)
  }
  return body as T
}

/** Unwrap the common `{ data: T }` envelope. */
export function data<T>(envelope: unknown): T {
  if (!envelope || typeof envelope !== 'object' || !('data' in envelope)) throw new ApiError(502, 'invalid_envelope', 'The API returned an unexpected shape')
  return (envelope as { data: T }).data
}

export const endpointNames = Object.keys(endpoints) as EndpointName[]
